import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, KeyRound } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("New password and confirmation don't match");
    if (form.new_password === form.current_password) return toast.error("New password must be different from the current one");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white w-[calc(100vw-2rem)] sm:w-full" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[color:var(--brand-teal)]" />
            Change Password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Current Password</Label>
            <Input
              required
              type={show ? "text" : "password"}
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="h-10"
              data-testid="cp-current"
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">New Password</Label>
            <Input
              required
              type={show ? "text" : "password"}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="h-10"
              minLength={6}
              data-testid="cp-new"
              autoComplete="new-password"
            />
            <p className="text-[11px] text-slate-500">Minimum 6 characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Confirm New Password</Label>
            <Input
              required
              type={show ? "text" : "password"}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="h-10"
              data-testid="cp-confirm"
              autoComplete="new-password"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="cp-show" />
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            Show passwords
          </label>
          <DialogFooter className="pt-1 flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white"
              data-testid="cp-save"
            >
              {saving ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
